import { Text, View } from "@react-pdf/renderer";
import { Style } from "@react-pdf/types";
import React from "react";
import { TFunction } from "react-i18next";
import {
  LOAD_DISTRIBUTION_PDF_FOOTER_SEGMENTS_LTR,
  LOAD_DISTRIBUTION_PDF_FOOTER_SEGMENTS_RTL,
  LOAD_DISTRIBUTION_PDF_TABLE_COLUMNS,
} from "../../data";
import { LoadDistributionPdfData } from "../../types";
import { resolvePdfValue } from "./InfoCard";
import { pdfStyles } from "./styles";

const withLastRowStyle = (rowStyle: Style, lastRowStyle: Style, isLast: boolean) =>
  isLast ? [rowStyle, lastRowStyle] : rowStyle;

export { withLastRowStyle };

const PdfTableHeaderCells = ({
  labels,
  cellStyle,
  wrap,
}: {
  labels: string[];
  cellStyle: Style;
  wrap?: boolean;
}) => (
  <View style={pdfStyles.pdfTableHeaderRow}>
    {labels.map((label) => (
      <Text key={label} style={cellStyle} wrap={wrap}>
        {label}
      </Text>
    ))}
  </View>
);

const PdfTableDetailCell = ({ label }: { label: string }) => (
  <View style={pdfStyles.calculationTableDetailCellWrap}>
    {label.includes(".") ? (
      <View style={pdfStyles.calculationTableDetailLabelRow}>
        {label.split("").map((character, index) => (
          <Text key={`${label}-${index}`} style={pdfStyles.pdfTableDataCell}>
            {character}
          </Text>
        ))}
      </View>
    ) : (
      <Text style={pdfStyles.pdfTableDataCell}>{label}</Text>
    )}
  </View>
);

export const PdfCalculationTable = ({
  t,
  pdfData,
}: {
  t: TFunction;
  pdfData: LoadDistributionPdfData;
}) => {
  const { calculationTableRows } = pdfData;

  return (
    <View style={pdfStyles.pdfTable} wrap={false}>
      <PdfTableHeaderCells
        labels={[
          t(LOAD_DISTRIBUTION_PDF_TABLE_COLUMNS.measurement),
          t(LOAD_DISTRIBUTION_PDF_TABLE_COLUMNS.calculation),
          t(LOAD_DISTRIBUTION_PDF_TABLE_COLUMNS.detail),
        ]}
        cellStyle={pdfStyles.pdfTableHeaderCell}
      />
      {calculationTableRows.map((row, index) => (
        <View
          key={row.labelKey}
          style={withLastRowStyle(
            pdfStyles.pdfTableBodyRow,
            pdfStyles.pdfTableBodyRowLast,
            index === calculationTableRows.length - 1
          )}
        >
          <Text style={pdfStyles.calculationTableValueCell}>{row.measurement}</Text>
          <Text style={pdfStyles.calculationTableValueCell}>{row.calculation}</Text>
          <PdfTableDetailCell label={t(row.labelKey)} />
        </View>
      ))}
    </View>
  );
};

export const PdfHydraulicWallTable = ({
  t,
  pdfData,
}: {
  t: TFunction;
  pdfData: LoadDistributionPdfData;
}) => {
  const { hydraulicWall } = pdfData;

  return (
    <View style={pdfStyles.hydraulicWallTableWrap}>
      <Text style={pdfStyles.pdfSectionTitleLeft}>{t(hydraulicWall.titleKey)}</Text>
      <View style={pdfStyles.pdfTable} wrap={false}>
        <PdfTableHeaderCells
          labels={[t(hydraulicWall.columns.chassisNoKey), t(hydraulicWall.columns.wallNumberKey)]}
          cellStyle={pdfStyles.pdfTableHeaderCellCompact}
          wrap={false}
        />
        {hydraulicWall.rows.map((row, index) => (
          <View
            key={`hydraulic-wall-${index}`}
            style={withLastRowStyle(
              pdfStyles.pdfTableBodyRow,
              pdfStyles.pdfTableBodyRowLast,
              index === hydraulicWall.rows.length - 1
            )}
          >
            <Text style={pdfStyles.pdfTableValueCell}>{resolvePdfValue(row.chassisNo, t)}</Text>
            <Text style={pdfStyles.pdfTableValueCell}>{resolvePdfValue(row.wallNumber, t)}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

export const PdfWeightDistributionTable = ({
  t,
  pdfData,
}: {
  t: TFunction;
  pdfData: LoadDistributionPdfData;
}) => {
  const { weightDistribution } = pdfData;
  const { columns } = weightDistribution;

  return (
    <View style={pdfStyles.weightDistributionTableWrap}>
      <View style={pdfStyles.pdfTableTitleRowRight}>
        <Text style={pdfStyles.pdfSectionTitleLtr}>{t(weightDistribution.titleSuffixKey)}</Text>
        <Text style={pdfStyles.pdfSectionTitle}>{t(weightDistribution.titleKey)}</Text>
      </View>
      <View style={pdfStyles.pdfTable} wrap={false}>
        <View style={pdfStyles.pdfTableHeaderRow}>
          {[columns.generalKey, columns.rearKey, columns.frontKey].map((key) => (
            <Text key={key} style={pdfStyles.pdfTableHeaderCell}>
              {t(key)}
            </Text>
          ))}
          <Text style={pdfStyles.pdfTableHeaderCellWide}>{t(columns.detailKey)}</Text>
        </View>
        {weightDistribution.rows.map((row, index) => (
          <View
            key={row.labelKey}
            style={withLastRowStyle(
              pdfStyles.pdfTableBodyRow,
              pdfStyles.pdfTableBodyRowLast,
              index === weightDistribution.rows.length - 1
            )}
          >
            <Text style={pdfStyles.pdfTableValueCell}>{row.general}</Text>
            <Text style={pdfStyles.pdfTableValueCell}>{row.rear}</Text>
            <Text style={pdfStyles.pdfTableValueCell}>{row.front}</Text>
            <Text style={pdfStyles.pdfTableDetailCell}>{t(row.labelKey)}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const PdfFooter = ({ t, language }: { t: TFunction; language: string }) => {
  const isRtl = (language || "hw").startsWith("hw") || language.startsWith("he");
  const segments = isRtl
    ? LOAD_DISTRIBUTION_PDF_FOOTER_SEGMENTS_RTL
    : LOAD_DISTRIBUTION_PDF_FOOTER_SEGMENTS_LTR;

  return (
    <View style={pdfStyles.pdfFooterRow}>
      {segments.map((segment) => (
        <Text
          key={segment.labelKey}
          style={segment.ltr ? pdfStyles.pdfFooterLtrText : pdfStyles.pdfFooterText}
        >
          {t(segment.labelKey)}
        </Text>
      ))}
    </View>
  );
};

export const PdfDualTablesSection = ({
  t,
  language,
  pdfData,
}: {
  t: TFunction;
  language: string;
  pdfData: LoadDistributionPdfData;
}) => (
  <View>
    <View style={pdfStyles.dualTablesRow}>
      <PdfHydraulicWallTable t={t} pdfData={pdfData} />
      <PdfWeightDistributionTable t={t} pdfData={pdfData} />
    </View>
    <PdfFooter t={t} language={language} />
  </View>
);
